/**
 * "Reset password" side pane shared by the tenant-admin and super-admin Users screens.
 *
 *   const resetPane = useResetPasswordPane({ api, onReset: fetchData });
 *   …
 *   selectionActions: { key:'reset', label:'Reset password', onClick: ([u]) => { closeOtherPanes(); resetPane.open(u); } }
 *   …
 *   <SplitLayout side={side || resetPane.side}>
 *
 * Step 1 — set a password (eye icon + Generate; empty = server generates one) and tick
 *          "Require password change at first login" (default on).
 * Step 2 — result: email, temporary password and login URL, each copyable; "Copy details"
 *          copies a ready-to-send message. No alert()/confirm() anywhere.
 */
import React, { useState } from 'react';
import { Key, CheckCircle, Copy, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { paneForm, PaneFooter } from './SidePane';
import PasswordField, { RequireChangeCheckbox, CopyField, copyText } from './PasswordField';
import { Button } from '../ui/button';

export function credentialsMessage({ email, password, loginUrl, requireChange, orgName }) {
  const lines = [
    `Your ${orgName ? orgName + ' ' : ''}account password has been reset.`,
    loginUrl ? `Login: ${loginUrl}` : null,
    email ? `Email: ${email}` : null,
    `Temporary password: ${password}`,
    requireChange ? 'You will be asked to set a new password the first time you sign in.' : null,
  ];
  return lines.filter(Boolean).join('\n');
}

function UserSummary({ user }) {
  if (!user) return null;
  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-muted/30 p-3" data-testid="reset-user-summary">
      <div className="w-9 h-9 rounded-full bg-shell-primary/10 text-shell-primary flex items-center justify-center text-xs font-semibold">
        {`${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()}
      </div>
      <div className="min-w-0 leading-tight">
        <div className="font-medium truncate">{user.first_name} {user.last_name}</div>
        <div className="text-xs text-muted-foreground truncate">{user.email || user.phone} · <span className="capitalize">{String(user.role || '').replace('_', ' ')}</span></div>
      </div>
    </div>
  );
}

export function useResetPasswordPane({ api, onReset, orgName }) {
  const [target, setTarget] = useState(null);
  const [password, setPassword] = useState('');
  const [requireChange, setRequireChange] = useState(true);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const open = (user) => { setTarget(user); setPassword(''); setRequireChange(true); setResult(null); };
  const close = () => { setTarget(null); setResult(null); setPassword(''); };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!target) return;
    if (password && password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/users/${target.id}/reset-password`, {
        password: password || null,
        require_change: requireChange,
      });
      setResult(res.data);
      toast.success('Password reset — share the new password with the user');
      onReset?.(res.data, target);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  let side = null;
  if (target && !result) {
    const f = paneForm({
      id: 'reset-password-form', onSubmit: submit, onCancel: close, submitLabel: 'Reset password', submitting, submitIcon: Key,
      children: (
        <>
          <UserSummary user={target} />
          <PasswordField
            label="New password"
            value={password}
            onChange={setPassword}
            generate
            placeholder="Leave empty to generate a temporary password"
            hint="At least 6 characters. Use Generate for a strong random password, or leave empty and the system will create one."
            testid="reset-password-input"
          />
          <RequireChangeCheckbox checked={requireChange} onCheckedChange={setRequireChange} testid="reset-require-change" />
          <p className="text-xs text-muted-foreground">
            The user's current password stops working immediately. You will see the new password on the next step so you can copy and share it securely.
          </p>
        </>
      ),
    });
    side = {
      open: true, onOpenChange: close, title: 'Reset password', subtitle: `${target.first_name} ${target.last_name}`, icon: Key,
      children: f.body, footer: f.footer, testid: 'reset-password-pane', storageKey: 'pane-reset-password',
    };
  } else if (target && result) {
    const message = credentialsMessage({
      email: result.email || target.email, password: result.temporary_password, loginUrl: result.login_url,
      requireChange: result.require_change, orgName,
    });
    side = {
      open: true, onOpenChange: close, title: 'Password reset', subtitle: `${target.first_name} ${target.last_name}`, icon: Key,
      testid: 'reset-password-result-pane', storageKey: 'pane-reset-password',
      children: (
        <div className="space-y-4" data-testid="reset-password-result">
          <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <CheckCircle className="w-5 h-5 flex-shrink-0 text-emerald-600" weight="fill" />
            <div>
              <div className="font-medium">New password is active</div>
              <div className="text-xs text-emerald-800/80">Share it with the user through a secure channel. It is not stored in plain text and cannot be shown again.</div>
            </div>
          </div>
          <CopyField label="Email" value={result.email || target.email || ''} testid="reset-result-email" />
          <CopyField label="Temporary password" value={result.temporary_password} secret revealed testid="reset-result-password" />
          {result.login_url && <CopyField label="Login URL" value={result.login_url} mono={false} testid="reset-result-url" />}
          <div className={`flex items-start gap-2 rounded-md border p-3 text-xs ${result.require_change ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-border bg-muted/30 text-muted-foreground'}`}>
            <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
            {result.require_change
              ? 'The user must set a new password the first time they sign in with this one.'
              : 'No password change is required at the next login.'}
          </div>
        </div>
      ),
      footer: (
        <PaneFooter
          onCancel={close} cancelLabel="Close"
          onSubmit={() => copyText(message, 'Login details')} submitLabel="Copy details" submitIcon={Copy}
          extra={<Button type="button" variant="ghost" size="sm" className="h-9" onClick={() => setResult(null)}>Reset again</Button>}
        />
      ),
    };
  }

  return { open, close, side, isOpen: !!target, target };
}
